import { DateTime, Interval } from 'luxon';
import { TimeTrackingEntry } from '../GraphQL/indexV2';

export const destructureEntries = (entries: TimeTrackingEntry[]) =>
	entries.map(({ jobNo, jobTaskNo, hours, onCall, startTime, endTime, startDate, endDate }) => ({
		jobNo,
		jobTaskNo,
		hours,
		onCall,
		startTime,
		endTime,
		startDate,
		endDate,
	}));

export const destructureAddonLines = (entries: TimeTrackingEntry[]) =>
	entries.flatMap(e =>
		e.addonLines.map(({ paymentSupplementCode, supplementType, jobNo, jobTaskNo, hours, onCall }) => ({
			paymentSupplementCode,
			supplementType,
			jobNo,
			jobTaskNo,
			hours,
			onCall,
			startTime: e.startTime,
			endTime: e.endTime,
		})),
	);

interface DestructuredEntries {
	jobNo: string | null;
	jobTaskNo: string;
	hours: number;
	onCall: boolean;
	startTime: string;
	endTime: string;
	startDate: string;
	endDate: string;
}

interface DestructuredAddonLines {
	paymentSupplementCode: string | null;
	jobNo: string | null;
	jobTaskNo: string;
	hours: number;
	onCall: boolean;
	startTime: string;
	endTime: string;
}

export interface OnCallHoursReturnType {
	hours: number;
	overtimeHours: number;
	eveningHours: number;
	nightHours: number;
	sundayHours: number;
}

export const toDateTimeString = (date: string, time: string) => {
	return `${date}T${time}`;
};

type RuleCheck = {
	isFullyInside: boolean;
	hasOverlap: boolean;
	overlapHours: number;
};

type RuleCheckInput = {
	onCallStartTime: DateTime;
	onCallEndTime: DateTime;
	actualEndTime: DateTime;
	ruleStart: DateTime;
	ruleEnd: DateTime;
};

const checkEntryAgainstRule = ({ onCallStartTime, onCallEndTime, actualEndTime, ruleStart, ruleEnd }: RuleCheckInput): RuleCheck => {
	// On-call compensation starts after actual work ends
	const payableStart = DateTime.max(onCallStartTime, actualEndTime);

	if (onCallEndTime <= payableStart || ruleEnd <= ruleStart) {
		return {
			isFullyInside: false,
			hasOverlap: false,
			overlapHours: 0,
		};
	}

	const payableInterval = Interval.fromDateTimes(payableStart, onCallEndTime);
	const ruleInterval = Interval.fromDateTimes(ruleStart, ruleEnd);
	const overlap = payableInterval.intersection(ruleInterval);

	return {
		isFullyInside: payableStart >= ruleStart && onCallEndTime <= ruleEnd,
		hasOverlap: overlap !== null,
		overlapHours: overlap ? overlap.toDuration(['hours', 'minutes']).as('hours') : 0,
	};
};

export const countOnCallHoursWithDates = (
	registerDate: string,
	entries: DestructuredEntries[],
	addonLines: DestructuredAddonLines[],
	jobTaskNo: string,
	onCallHourDiff: number,
): OnCallHoursReturnType => {
	// '2100', Overtid 1
	// '2101', Overtid 2
	// '2105', Overtid (INTERN)
	// '8599', Overtid (INTERN)
	const overTimeCodes = ['2100', '2101', '2105', '8599'];

	// '2051', Aften
	// '2052', Aften (INTERN)
	// Time frame: 18 - 22
	// const eveningCodes = ['2051', '2052'];

	// '2054', Nat
	// '2055', Nat (INTERN)
	// Time frame: 22 - 23:59 and 00 - 05
	// const nightCodes = ['2054', '2055'];

	// '2056', Sunday
	// '2057', Sunday (INTERN)
	// day 6
	// const sundayCodes = ['2056', '2057'];

	let spilloverEveningHours = 0;
	let spilloverNightHours = 0;
	let sundayHours = 0;

	const calcOvertimeHours = addonLines.reduce((acc, curr) => {
		if (curr.jobTaskNo === jobTaskNo && curr.onCall && overTimeCodes.includes(curr.paymentSupplementCode ?? '')) {
			const difference = onCallHourDiff - curr.hours;
			return acc + difference;
		}

		return acc;
	}, 0);

	const hours = entries.reduce((acc, curr) => {
		if (curr.jobTaskNo === jobTaskNo && curr.onCall) {
			const difference = onCallHourDiff - curr.hours;

			// The date you register on
			const registerDay = DateTime.fromISO(registerDate);
			// Actual end time of the entry
			const endDateTime = DateTime.fromISO(toDateTimeString(curr.endDate, curr.endTime));
			// Start time of the on-call period (same as start time of the entry)
			const onCallStartTime = DateTime.fromISO(toDateTimeString(curr.startDate, curr.startTime));
			// End time of the on-call period (calculated by adding the difference to the end time of the entry)
			const onCallEndTime = DateTime.fromISO(toDateTimeString(curr.endDate, curr.endTime)).plus({ hours: difference });

			// Rule boundaries
			// Early night: 00:00 - 05:00 on register day
			const registerDayEarlyNightStartRule = registerDay.set({ hour: 0, minute: 0 });
			const registerDayEarlyNightEndRule = registerDay.set({ hour: 5, minute: 0 });
			// Day: 05:00 - 18:00 on register day
			const registerDayDayStartRule = registerDay.set({ hour: 5, minute: 0 });
			const registerDayDayEndRule = registerDay.set({ hour: 18, minute: 0 });
			// Evening: 18:00 - 22:00 on register day
			const registerDayEveningStartRule = registerDay.set({ hour: 18, minute: 0 });
			const registerDayEveningEndRule = registerDay.set({ hour: 22, minute: 0 });
			// Late night: 22:00 on register day - 05:00 on the next day
			const registerDayLateNightStartRule = registerDay.set({ hour: 22, minute: 0 });
			const registerDayLateNightEndRule = registerDay.plus({ days: 1 }).set({ hour: 5, minute: 0 });
			// Next day rules for day and evening
			const dayAfterDayStartRule = registerDay.plus({ days: 1 }).set({ hour: 5, minute: 0 });
			const dayAfterDayEndRule = registerDay.plus({ days: 1 }).set({ hour: 18, minute: 0 });

			// Case 1: Check if the end time is a saturday or sunday
			const dayOfWeek = registerDay.weekday;
			if (dayOfWeek === 7 || (dayOfWeek === 6 && onCallEndTime.hour > 0 && onCallEndTime.hour <= 5)) {
				console.log('Case 1: Saturday or sunday');
				sundayHours += difference;
			}

			// Case 2: If the onCallEndTime is before eveningStart (18:00)
			if (onCallEndTime <= registerDayEveningStartRule && onCallEndTime >= registerDayEveningEndRule) {
				console.log('Case 2: Before evening');
				const eveningStartTime = DateTime.max(endDateTime, registerDayEveningStartRule);
				const eveningHours = Math.min(onCallEndTime.diff(eveningStartTime, ['hours', 'minutes']).hours + onCallEndTime.diff(eveningStartTime, ['hours', 'minutes']).minutes / 60, difference);

				spilloverEveningHours += Math.max(0, eveningHours);

				return (acc += difference);
			}

			const earlyNightRuleCheck = checkEntryAgainstRule({
				onCallStartTime,
				onCallEndTime,
				actualEndTime: endDateTime,
				ruleStart: registerDayEarlyNightStartRule, // 22:00 on register day
				ruleEnd: registerDayEarlyNightEndRule, // 05:00 next day
			});
			spilloverNightHours += earlyNightRuleCheck.overlapHours;

			const dayRuleCheck = checkEntryAgainstRule({
				onCallStartTime,
				onCallEndTime,
				actualEndTime: endDateTime,
				ruleStart: registerDayDayStartRule,
				ruleEnd: registerDayDayEndRule,
			});
			acc += dayRuleCheck.overlapHours;

			const eveningRuleCheck = checkEntryAgainstRule({
				onCallStartTime,
				onCallEndTime,
				actualEndTime: endDateTime,
				ruleStart: registerDayEveningStartRule,
				ruleEnd: registerDayEveningEndRule,
			});
			spilloverEveningHours += eveningRuleCheck.overlapHours;

			const nightRuleCheck = checkEntryAgainstRule({
				onCallStartTime,
				onCallEndTime,
				actualEndTime: endDateTime,
				ruleStart: registerDayLateNightStartRule, // 22:00 on register day
				ruleEnd: registerDayLateNightEndRule, // 05:00 next day
			});
			spilloverNightHours += nightRuleCheck.overlapHours;

			const dayAfterRuleCheck = checkEntryAgainstRule({
				onCallStartTime,
				onCallEndTime,
				actualEndTime: endDateTime,
				ruleStart: dayAfterDayStartRule, // 22:00 on register day
				ruleEnd: dayAfterDayEndRule, // 05:00 next day
			});
			acc += dayAfterRuleCheck.overlapHours;

			return acc + nightRuleCheck.overlapHours + eveningRuleCheck.overlapHours + earlyNightRuleCheck.overlapHours;
		}

		return acc;
	}, 0);

	return {
		hours: hours,
		overtimeHours: calcOvertimeHours,
		eveningHours: spilloverEveningHours,
		nightHours: spilloverNightHours,
		sundayHours: sundayHours,
	};
};

